<template>
    <div>
        <NuxtLayout name="tema">
            <template #leftPanel>
                <div
                    v-if="book"
                    ref="bookRef"
                    class="flex flex-col gap-2"
                >
                    <div
                        ref="bookInfo"
                        class="sticky top-0 flex w-full items-start justify-between bg-white"
                    >
                        <div>
                            <NuxtLink :to="`/texty/${book.kod}`">
                                <h1 class="page-title mb-0">
                                    {{ book.title }}
                                </h1>
                                <h1 class="heading-2">({{ book.kod }})</h1>
                            </NuxtLink>
                            <h4 class="heading-4">
                                {{ book.subtitle }}
                            </h4>
                            <div class="text-weak">
                                {{ $t("texty.aktualni") }} 12/2024
                            </div>
                            <img
                                class="size-20"
                                :src="`/img/sklicka_250x250/${book.kod.padStart(4, '0')}_250x250.webp`"
                                :alt="book?.title"
                            />
                        </div>
                        <div class="relative flex items-center gap-2 px-2 py-2">
                            <div
                                v-if="locale === 'en'"
                                v-tooltip.right="
                                    'The English version provides a summary of the original texts, focusing on a larger, metropolitan scale. For full details, please refer to the Czech version.'
                                "
                            >
                                <Icon
                                    name="custom:info"
                                    size="1.25rem"
                                />
                            </div>
                            <NuxtLink
                                v-if="locale === 'cs'"
                                v-tooltip.right="'Stáhnout celou knihu'"
                                :to="booksStore.getBookFilePath(book.kod)"
                                target="_blank"
                                class="cursor-pointer"
                            >
                                <Icon
                                    name="custom:stahovani"
                                    size="1.25rem"
                                />
                            </NuxtLink>
                        </div>
                    </div>
                    <TextyObsah
                        v-if="contents && tema"
                        :contents="contents"
                        :active="tema?.KOD"
                        :style="{
                            height: `calc(100vh - ${store.headerHeight + bookInfoHeight + 8}px)`,
                        }"
                    />
                </div>
            </template>
            <div
                v-if="tema && contents"
                class="flex max-w-prose flex-col"
            >
                <h1 class="heading-1">
                    {{ tema.TEMA }}
                    {{
                        contents.find(
                            item => `${item.kniha}.${item.kod}` === tema?.KOD
                        )?.nazev
                    }}
                </h1>
                <div
                    v-if="tema.METADATA_TEMA"
                    class="my-4 flex flex-col font-proregular"
                >
                    <div>
                        {{ $t("texty.garant") }}:
                        <NuxtLink
                            :to="
                                'mailto:' + tema.METADATA_TEMA[0]?.GARANT_EMAIL
                            "
                            class="underline underline-offset-2"
                            >{{
                                tema.METADATA_TEMA[0]?.GARANT ||
                                tema.METADATA_TEMA[0]?.JMENO_AUTORA
                            }}</NuxtLink
                        >
                    </div>
                    <div>
                        {{ $t("texty.autor") }}:
                        {{ tema.METADATA_TEMA[0]?.JMENO_AUTORA
                        }}{{
                            tema.METADATA_TEMA[0]?.JMENO_SPOLUAUTORA
                                ? ` / ${tema.METADATA_TEMA[0]?.JMENO_SPOLUAUTORA.replace("\n", ", ").replaceAll("; ", ", ")}`
                                : ""
                        }}
                    </div>
                </div>
                <div
                    v-if="tema.METADATA_TEMA"
                    class="my-4 flex flex-col gap-1 font-proregular"
                >
                    <TagItemList
                        :content="tema.METADATA_TEMA[0]?.KLICOVA_SLOVA"
                        type="klic_slova"
                        empty-behavior="hidden"
                    />
                    <TagItemList
                        v-if="locale === 'cs'"
                        :content="tema.METADATA_TEMA[0]?.JEVY"
                        type="jevy"
                        empty-behavior="hidden"
                    />
                    <TagItemList
                        :content="tema.METADATA_TEMA[0]?.DATOVE_VRSTVY"
                        type="dat_vrstvy"
                        empty-behavior="hidden"
                    />
                </div>
                <TextyTextParts
                    :text="tema.HTML"
                    :tema="tema"
                />
            </div>
            <div
                v-if="tema"
                class="items-strech bottom-0 z-40 mb-20 flex min-h-20 w-full max-w-prose justify-between divide-x divide-black-text border-t border-black-text bg-white py-4 print:hidden"
            >
                <NuxtLink
                    :to="
                        localePath(
                            `/texty/${previousTema?.kniha}/${previousTema?.kod}`
                        )
                    "
                    class="flex w-1/2 items-center pr-2 text-left font-promedium text-sm opacity-0 hover:text-hover-link"
                    :class="{ 'opacity-100': previousTema }"
                >
                    <div class="flex w-full shrink-0 items-center gap-2">
                        <i class="pi pi-arrow-left" />
                        <span>{{ previousTema?.nazev }}</span>
                    </div>
                </NuxtLink>
                <NuxtLink
                    :to="
                        localePath(`/texty/${nextTema?.kniha}/${nextTema?.kod}`)
                    "
                    class="flex w-1/2 items-center justify-end pl-2 text-right font-promedium text-sm opacity-0 hover:text-hover-link"
                    :class="{ 'opacity-100': nextTema }"
                >
                    <div
                        class="flex w-full shrink-0 items-center justify-end gap-2"
                    >
                        <span>{{ nextTema?.nazev }}</span>
                        <i class="pi pi-arrow-right" />
                    </div>
                </NuxtLink>
            </div>
        </NuxtLayout>
    </div>
</template>

<script lang="ts" setup>
const { locale } = useI18n()
const localePath = useLocalePath()
const store = useMainStore()
const booksStore = useBooksStore()

const { temaKod, book } = defineProps<{
    book: BookItem
    temaKod: string
}>()

const bookRef = ref<HTMLElement | null>(null)
const bookInfo = ref<HTMLElement | null>(null)
const bookInfoHeight = ref(0)

await useAsyncData(`tema-${temaKod}`, async () => {
    return await booksStore.fetchTemaData(
        locale.value as LanguageCode,
        temaKod,
        book.kod
    )
})

onMounted(async () => {
    await nextTick()
    await checkIfElementExists(bookInfo)
    // await until(book).toBeTruthy()
    bookInfoHeight.value = bookInfo.value?.clientHeight || 0
})

/* watch(locale, () => {
    setTimeout(async () => {
        await navigateTo(localePath(`/texty`))
    }, 300)
}) */
watch(locale, async () => {
    await navigateTo(localePath(`/texty`))
})

const contents = computed(() => {
    return booksStore.bookTrees[locale.value as LanguageCode][book.kod]
})

const tema = computed(() => {
    let tema =
        booksStore.temaData[locale.value as LanguageCode][
            `${book.kod}.${temaKod}`
        ]
    if (!tema) {
        const idx = contents.value?.findIndex(item => item.kod === temaKod)
        if (idx === undefined || idx === -1 || !contents.value) return undefined
        const nextTema = contents.value
            .slice(idx + 1)
            .find(item => item.style === "uap04")
        if (!nextTema) return undefined
        tema =
            booksStore.temaData[locale.value as LanguageCode][
                `${book.kod}.${nextTema.kod}`
            ]
    }
    /* if (!tema) {
        tema =
            booksStore.temaData[locale.value as LanguageCode][
                `${book.kod}.${temaKod}.1.1`
            ]
    } */
    if (!tema) {
        console.warn(`Tema ${book.kod}.${temaKod} not found`)
        return undefined
    }
    return tema
})

const previousTema = computed(() => {
    if (!contents.value || !tema.value) return null
    const temata = contents.value.filter(el => el.style === "uap04")
    const index = temata.findIndex(item => item.kod === tema.value?.TEMA)
    // if (index === -1) return null
    if (
        !temata[index - 1] &&
        tema.value.TEMA !== "1" &&
        tema.value.TEMA !== "1.1"
    ) {
        const temataExtended = contents.value.filter(
            el => el.style === "uap04" || el.style === "uap02"
        )
        const indexExtended = temataExtended.findIndex(
            item => item.kod === tema.value?.TEMA
        )
        return temataExtended[indexExtended - 1] || null
    }
    return temata[index - 1] || null
})

const nextTema = computed(() => {
    if (!contents.value || !tema.value) return null
    const temata = contents.value.filter(el => el.style === "uap04")
    const index = temata.findIndex(item => item.kod === tema.value?.TEMA)
    // if (index === temata.length) return null
    if (!temata[index + 1]) {
        const temataExtended = contents.value.filter(
            el => el.style === "uap04" || el.style === "uap02"
        )
        const indexExtended = temataExtended.findIndex(
            item => item.kod === tema.value?.TEMA
        )
        return temataExtended[indexExtended + 1] || null
    }
    return temata[index + 1]
})
</script>

<style scoped></style>
